import React, { useEffect, useMemo, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Checkbox } from "@/components/ui/checkbox"
import { CheckCircle2, Circle, Copy, ExternalLink, FileText, Inbox, Link2, RefreshCw, Sparkles } from 'lucide-react'
import { Client } from '@/hooks/useClients'
import { useToast } from "@/hooks/use-toast"
import { supabase } from '@/integrations/supabase/client'
import { IntakeSubmission, str, normalizeDomain, answersOf, crmValueOf, buildNotesBlock } from './intake-prefill'

// Intake-driven onboarding: replaces the 8-step ClientOnboardingWizard as the
// Onboarding tab. The client-answered half of onboarding now arrives via the
// self-hosted intake form (intake_submissions); this view renders the
// submission, applies mapped fields to the client record, and tracks the
// internal-only leftovers as a checklist deep-linking the existing tabs.
// The old wizard component is kept in the repo untouched for rollback.

const INTAKE_FORM_URL = 'https://intake.outboundbuddy.com'

interface ClientIntakeOnboardingProps {
  client: Client
  // Switches the parent's active tab (internal checklist deep links).
  onNavigateTab?: (tab: string) => void
}

// ---------- helpers ----------

// Fields already shown in the structured sections; anything else the form
// sends lands in "Other answers" so new form fields are never invisible here.
const KNOWN_KEYS = new Set([
  'companyName', 'website', 'contactName', 'contactRole', 'contactEmail', 'contactPhone', 'approver',
  'hasRevOps', 'revOpsName', 'revOpsRole', 'revOpsEmail', 'otherStakeholders',
  'crm', 'crmOther', 'hasBlocklist', 'blocklist', 'hasAvoid', 'avoidAccounts', 'avoidAccountsFile',
  'sendingState', 'sendingSetup', 'otherTools',
  'researchState', 'researchResult', 'icpMatch', 'icpChanges', 'icp', 'competitors',
  'offering', 'priorOutbound', 'priorLearnings', 'successMetric', 'targetAccounts',
  'hardDeadlines', 'extraNotes', 'referralSource', 'referralWho', 'answers',
])

const display = (v: unknown): string =>
  v == null ? '' : typeof v === 'string' ? v : JSON.stringify(v, null, 2)

// ---------- field mapping (intake -> clients columns) ----------

interface FieldProposal {
  column: string
  label: string
  intakeValue: string
  currentValue: string | null
}

function buildFieldProposals(client: Client, a: Record<string, unknown>): FieldProposal[] {
  const crmValue = crmValueOf(a)

  const candidates: Array<[string, string, string | null, unknown]> = [
    ['company', 'Company', str(a.companyName), (client as any).company],
    ['website', 'Website', str(a.website), (client as any).website],
    ['main_contact_email', 'Main contact email', str(a.contactEmail), (client as any).main_contact_email],
    ['crm_system', 'CRM system', crmValue, (client as any).crm_system],
    ['product_description', 'Product / offering', str(a.offering), (client as any).product_description],
  ]

  return candidates
    .filter(([, , intake]) => intake != null)
    .map(([column, label, intake, current]) => ({
      column,
      label,
      intakeValue: intake as string,
      currentValue: str(current),
    }))
}

interface MemberProposal {
  name: string
  email: string | null
  phone: string | null
  title: string | null
  is_primary_contact: boolean
}

function buildMemberProposals(a: Record<string, unknown>, existingEmails: Set<string>): MemberProposal[] {
  const proposals: MemberProposal[] = []
  const seen = new Set(existingEmails)

  const push = (name: string | null, email: string | null, phone: string | null, title: string | null, primary: boolean) => {
    const key = email?.toLowerCase()
    if (!name && !email) return
    if (key && seen.has(key)) return
    if (key) seen.add(key)
    proposals.push({ name: name || email || '', email, phone, title, is_primary_contact: primary })
  }

  push(str(a.contactName), str(a.contactEmail), str(a.contactPhone), str(a.contactRole) || 'Day-to-day contact', true)
  if (str(a.hasRevOps) === 'yes') {
    push(str(a.revOpsName), str(a.revOpsEmail), null, str(a.revOpsRole) || 'RevOps', false)
  }
  if (str(a.approver)) {
    push(str(a.approver), null, null, 'Decision maker', false)
  }
  for (const email of (str(a.otherStakeholders) || '').split(',').map(e => e.trim()).filter(Boolean)) {
    if (/\S+@\S+\.\S+/.test(email)) push(null, email, null, 'Stakeholder', false)
  }
  return proposals
}

// ---------- internal checklist (the half the intake form can't answer) ----------

interface ChecklistItem {
  label: string
  detail: string
  tab: string
  done: boolean
}

function buildChecklist(client: Client): ChecklistItem[] {
  const c = client as any
  return [
    {
      label: 'Core info (package, products, capacity)',
      detail: 'Products purchased, retainer, send capacity',
      tab: 'core-info',
      done: !!(c.products && c.products.length > 0),
    },
    {
      label: 'Slack channels',
      detail: 'General, positive and negative reply channels created',
      tab: 'communication',
      // Leadership and reporting were retired 2026-09-02, so this can no longer
      // require them -- a new client would never tick this box.
      done: !!(c.slack_sales_channel && c.slack_positive_replies_channel && c.slack_neutral_negative_replies_channel),
    },
    {
      label: 'TAM & blocklist',
      detail: 'TAM uploaded, blocklist configured',
      tab: 'tam',
      done: !!(c.tam_accounts_url || c.tam_list_url || c.tam_uploaded_data),
    },
    {
      label: 'Meeting links',
      detail: 'Calendar booking links for the sales team',
      tab: 'calendar',
      done: !!(c.calendar_links && Object.keys(c.calendar_links).length > 0),
    },
    {
      label: 'External systems & links',
      detail: 'HubSpot record, ClickUp backlog, system IDs',
      tab: 'external-systems',
      done: !!(c.hubspot_record_url || c.clickup_backlog_url),
    },
  ]
}

// ---------- component ----------

export function ClientIntakeOnboarding({ client, onNavigateTab }: ClientIntakeOnboardingProps) {
  const [linked, setLinked] = useState<IntakeSubmission | null>(null)
  const [unlinked, setUnlinked] = useState<IntakeSubmission[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isApplying, setIsApplying] = useState(false)
  const [showApply, setShowApply] = useState(false)
  const [checkedColumns, setCheckedColumns] = useState<Record<string, boolean>>({})
  const [checkedMembers, setCheckedMembers] = useState<Record<number, boolean>>({})
  const [includeNotes, setIncludeNotes] = useState(true)
  const [existingEmails, setExistingEmails] = useState<Set<string>>(new Set())
  const { toast } = useToast()

  const load = async () => {
    setIsLoading(true)
    try {
      const [linkedRes, unlinkedRes, membersRes] = await Promise.all([
        (supabase as any).from('intake_submissions').select('*')
          .eq('linked_client_id', client.id)
          .order('created_at', { ascending: false }).limit(1),
        (supabase as any).from('intake_submissions').select('*')
          .is('linked_client_id', null)
          .order('created_at', { ascending: false }).limit(25),
        supabase.from('client_team_members').select('email')
          .eq('client_id', client.id),
      ])
      setLinked((linkedRes.data?.[0] as IntakeSubmission) || null)
      setUnlinked((unlinkedRes.data as IntakeSubmission[]) || [])
      setExistingEmails(new Set(
        (membersRes.data || []).map((m: any) => m.email?.toLowerCase()).filter(Boolean)
      ))
    } catch (error) {
      console.error('Error loading intake submissions:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => { load() }, [client.id])

  const clientDomain = normalizeDomain((client as any).website) || normalizeDomain((client as any).domain)
  const clientNameLc = (client.name || '').toLowerCase()
  const companyLc = ((client as any).company || '').toLowerCase()

  const isMatch = (s: IntakeSubmission) => {
    const subDomain = normalizeDomain(s.website)
    if (subDomain && clientDomain && subDomain === clientDomain) return true
    const subCompany = (s.company_name || '').toLowerCase()
    if (subCompany && (subCompany.includes(clientNameLc) || clientNameLc.includes(subCompany)
      || (companyLc && (subCompany.includes(companyLc) || companyLc.includes(subCompany))))) return true
    return false
  }

  const candidates = useMemo(() => unlinked.filter(isMatch), [unlinked, client])
  const others = useMemo(() => unlinked.filter(s => !isMatch(s)), [unlinked, client])

  const answers = linked ? answersOf(linked) : {}
  const fieldProposals = useMemo(
    () => linked ? buildFieldProposals(client, answers) : [],
    [linked, client]
  )
  const memberProposals = useMemo(
    () => linked ? buildMemberProposals(answers, existingEmails) : [],
    [linked, existingEmails]
  )
  const notesBlock = linked ? buildNotesBlock(linked, answers) : ''
  const checklist = buildChecklist(client)

  const openApply = () => {
    // Fill-empty-only pre-checked; conflicts (column already has a different
    // value) start unchecked so nothing is silently overwritten.
    const initial: Record<string, boolean> = {}
    for (const p of fieldProposals) {
      initial[p.column] = p.currentValue == null || p.currentValue === p.intakeValue
    }
    setCheckedColumns(initial)
    setCheckedMembers(Object.fromEntries(memberProposals.map((_, i) => [i, true])))
    setIncludeNotes(!!notesBlock)
    setShowApply(true)
  }

  const handleLink = async (submission: IntakeSubmission) => {
    const { error } = await supabase
      .from('intake_submissions')
      .update({ linked_client_id: client.id })
      .eq('id', submission.id)
    if (error) {
      toast({ title: "Error", description: "Failed to link submission", variant: "destructive" })
      return
    }
    toast({ title: "Linked", description: `Intake submission linked to ${client.name}` })
    await load()
  }

  const handleApply = async () => {
    if (!linked) return
    setIsApplying(true)
    try {
      const updates: Record<string, unknown> = {}
      for (const p of fieldProposals) {
        if (checkedColumns[p.column] && p.currentValue !== p.intakeValue) {
          updates[p.column] = p.intakeValue
        }
      }
      if (includeNotes && notesBlock) {
        const current = str((client as any).onboarding_notes)
        if (!current?.includes(notesBlock.split('\n')[0])) {
          updates.onboarding_notes = current ? `${current}\n\n${notesBlock}` : notesBlock
        }
      }

      if (Object.keys(updates).length > 0) {
        const { error } = await supabase.from('clients').update(updates).eq('id', client.id)
        if (error) throw error
      }

      const membersToInsert = memberProposals
        .filter((_, i) => checkedMembers[i])
        .map(m => ({
          client_id: client.id,
          name: m.name,
          email: m.email,
          phone: m.phone,
          title: m.title,
          is_primary_contact: m.is_primary_contact && existingEmails.size === 0,
          status: 'active',
        }))
      if (membersToInsert.length > 0) {
        const { error } = await supabase.from('client_team_members').insert(membersToInsert)
        if (error) throw error
      }

      const { error: markError } = await supabase
        .from('intake_submissions')
        .update({ applied_at: new Date().toISOString() })
        .eq('id', linked.id)
      if (markError) throw markError

      toast({
        title: "Applied",
        description: `${Object.keys(updates).length} field(s) updated, ${membersToInsert.length} team member(s) added. Reloading…`,
      })
      // The client prop is owned by the parent page; a reload is the reliable
      // way to show the freshly applied values everywhere.
      setTimeout(() => window.location.reload(), 1200)
    } catch (error) {
      console.error('Error applying intake submission:', error)
      toast({ title: "Error", description: "Failed to apply submission", variant: "destructive" })
      setIsApplying(false)
    }
  }

  const copyFormLink = () => {
    navigator.clipboard.writeText(INTAKE_FORM_URL)
    toast({ title: "Copied", description: INTAKE_FORM_URL })
  }

  const renderValue = (label: string, v: unknown) => {
    const val = display(v)
    if (!val) return null
    return (
      <div key={label} className="grid grid-cols-3 gap-2 py-1.5 border-b last:border-0">
        <span className="text-sm text-muted-foreground">{label}</span>
        <span className="col-span-2 text-sm whitespace-pre-wrap break-words">{val}</span>
      </div>
    )
  }

  const submissionCard = (s: IntakeSubmission, matched: boolean) => (
    <div key={s.id} className="flex items-center justify-between border rounded-md p-3">
      <div>
        <div className="font-medium text-sm">
          {s.company_name || s.contact_email || 'Unnamed submission'}
          {matched && <Badge className="ml-2" variant="secondary">Likely match</Badge>}
        </div>
        <div className="text-xs text-muted-foreground">
          {[s.website, s.contact_email, new Date(s.created_at).toLocaleDateString()].filter(Boolean).join(' · ')}
        </div>
      </div>
      <Button size="sm" variant={matched ? "default" : "outline"} onClick={() => handleLink(s)}>
        <Link2 className="h-4 w-4 mr-1" /> Link
      </Button>
    </div>
  )

  if (isLoading) {
    return <div className="text-center text-muted-foreground py-8">Loading intake submissions…</div>
  }

  return (
    <div className="space-y-6">
      {/* ---- Intake submission ---- */}
      {!linked ? (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Inbox className="h-4 w-4" /> No intake submission linked yet
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Send the client the intake form. Their submission will appear below when it arrives.
              </p>
              <div className="flex items-center gap-2">
                <code className="text-sm bg-muted px-2 py-1 rounded">{INTAKE_FORM_URL}</code>
                <Button size="sm" variant="outline" onClick={copyFormLink}>
                  <Copy className="h-4 w-4 mr-1" /> Copy link
                </Button>
                <Button size="sm" variant="ghost" onClick={load}>
                  <RefreshCw className="h-4 w-4 mr-1" /> Check again
                </Button>
              </div>
            </CardContent>
          </Card>

          {(candidates.length > 0 || others.length > 0) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Unlinked submissions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {candidates.map(s => submissionCard(s, true))}
                {others.map(s => submissionCard(s, false))}
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between text-base">
              <span className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Intake submission · {new Date(linked.created_at).toLocaleDateString()}
                {linked.research_state === 'done'
                  ? <Badge variant="secondary"><Sparkles className="h-3 w-3 mr-1" />ICP researched</Badge>
                  : <Badge variant="outline">Manual ICP</Badge>}
                {linked.applied_at && <Badge className="bg-green-100 text-green-800">Applied {new Date(linked.applied_at).toLocaleDateString()}</Badge>}
              </span>
              {!linked.applied_at && (
                <Button size="sm" onClick={openApply} disabled={showApply}>
                  Apply to client record
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="text-sm font-semibold mb-1">Company</h4>
              {renderValue('Company', answers.companyName)}
              {renderValue('Website', answers.website)}
              {renderValue('Contact', answers.contactName)}
              {renderValue('Role', answers.contactRole)}
              {renderValue('Email', answers.contactEmail)}
              {renderValue('Phone', answers.contactPhone)}
              {renderValue('Decision maker', answers.approver)}
            </div>
            <div>
              <h4 className="text-sm font-semibold mb-1">Team</h4>
              {renderValue('Has RevOps', answers.hasRevOps)}
              {renderValue('RevOps', answers.revOpsName)}
              {renderValue('RevOps role', answers.revOpsRole)}
              {renderValue('RevOps email', answers.revOpsEmail)}
              {renderValue('Other stakeholders', answers.otherStakeholders)}
            </div>
            <div>
              <h4 className="text-sm font-semibold mb-1">Setup</h4>
              {renderValue('CRM', answers.crm)}
              {renderValue('CRM (other)', answers.crmOther)}
              {renderValue('Blocklist', answers.hasBlocklist)}
              {renderValue('Blocklist link/file', answers.blocklist)}
              {renderValue('Accounts to avoid', answers.avoidAccounts)}
              {renderValue('Avoid list file', answers.avoidAccountsFile)}
              {renderValue('Sending state', answers.sendingState)}
              {renderValue('Sending setup', answers.sendingSetup)}
              {renderValue('Other tools', answers.otherTools)}
            </div>
            <div>
              <h4 className="text-sm font-semibold mb-1">Campaign</h4>
              {linked.research_state === 'done' ? (
                <>
                  {renderValue('Research result', answers.researchResult)}
                  {renderValue('ICP match', answers.icpMatch)}
                  {renderValue('ICP corrections', answers.icpChanges)}
                </>
              ) : (
                <>
                  {renderValue('ICP (client-written)', answers.icp)}
                  {renderValue('Competitors', answers.competitors)}
                </>
              )}
              {renderValue('Offering', answers.offering)}
              {renderValue('Prior outbound', answers.priorOutbound)}
              {renderValue('Prior data location', answers.priorLearnings)}
              {renderValue('Success metric', answers.successMetric)}
              {renderValue('Target accounts', answers.targetAccounts)}
            </div>
            <div>
              <h4 className="text-sm font-semibold mb-1">Timing</h4>
              {renderValue('Hard deadlines', answers.hardDeadlines)}
              {renderValue('Extra notes', answers.extraNotes)}
              {renderValue('Referral source', answers.referralSource)}
              {renderValue('Referred by', answers.referralWho)}
            </div>
            {Object.keys(answers).filter(k => !KNOWN_KEYS.has(k)).length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-1">Other answers</h4>
                {Object.entries(answers)
                  .filter(([k]) => !KNOWN_KEYS.has(k))
                  .map(([k, v]) => renderValue(k, v))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ---- Apply review ---- */}
      {linked && showApply && !linked.applied_at && (
        <Card className="border-primary">
          <CardHeader>
            <CardTitle className="text-base">Review before applying</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <h4 className="text-sm font-semibold">Client record fields</h4>
              {fieldProposals.length === 0 && (
                <p className="text-sm text-muted-foreground">No mappable fields in this submission.</p>
              )}
              {fieldProposals.map(p => {
                const conflict = p.currentValue != null && p.currentValue !== p.intakeValue
                const noop = p.currentValue === p.intakeValue
                return (
                  <label key={p.column} className="flex items-start gap-2 text-sm border rounded-md p-2 cursor-pointer">
                    <Checkbox
                      checked={!!checkedColumns[p.column]}
                      disabled={noop}
                      onCheckedChange={(v) => setCheckedColumns(prev => ({ ...prev, [p.column]: !!v }))}
                    />
                    <span className="flex-1">
                      <span className="font-medium">{p.label}</span>
                      {noop ? (
                        <span className="text-muted-foreground"> — already matches ({p.intakeValue})</span>
                      ) : conflict ? (
                        <span> — <span className="line-through text-muted-foreground">{p.currentValue}</span>{' '}
                          <span className="text-orange-600 font-medium">→ {p.intakeValue}</span>
                          <Badge variant="outline" className="ml-2 text-orange-600 border-orange-300">overwrites</Badge>
                        </span>
                      ) : (
                        <span className="text-muted-foreground"> — fills empty with "{p.intakeValue}"</span>
                      )}
                    </span>
                  </label>
                )
              })}
            </div>

            {memberProposals.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold">New team members</h4>
                {memberProposals.map((m, i) => (
                  <label key={i} className="flex items-center gap-2 text-sm border rounded-md p-2 cursor-pointer">
                    <Checkbox
                      checked={!!checkedMembers[i]}
                      onCheckedChange={(v) => setCheckedMembers(prev => ({ ...prev, [i]: !!v }))}
                    />
                    <span>{m.name}{m.email ? ` · ${m.email}` : ''}{m.title ? ` · ${m.title}` : ''}</span>
                  </label>
                ))}
                <p className="text-xs text-muted-foreground">Members whose email already exists on this client are skipped automatically.</p>
              </div>
            )}

            {notesBlock && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold">Onboarding notes</h4>
                <label className="flex items-start gap-2 text-sm border rounded-md p-2 cursor-pointer">
                  <Checkbox checked={includeNotes} onCheckedChange={(v) => setIncludeNotes(!!v)} />
                  <pre className="flex-1 text-xs whitespace-pre-wrap font-sans text-muted-foreground">{notesBlock}</pre>
                </label>
              </div>
            )}

            <div className="flex gap-2">
              <Button onClick={handleApply} disabled={isApplying}>
                {isApplying ? 'Applying…' : 'Apply selected'}
              </Button>
              <Button variant="outline" onClick={() => setShowApply(false)} disabled={isApplying}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ---- Internal checklist ---- */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Internal setup (not asked on the form)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1">
          {checklist.map(item => (
            <button
              key={item.tab}
              type="button"
              className={`w-full flex items-center justify-between rounded-md p-2 text-left ${onNavigateTab ? 'hover:bg-muted cursor-pointer' : 'cursor-default'}`}
              onClick={() => onNavigateTab?.(item.tab)}
            >
              <span className="flex items-center gap-3">
                {item.done
                  ? <CheckCircle2 className="h-5 w-5 text-green-600" />
                  : <Circle className="h-5 w-5 text-muted-foreground" />}
                <span>
                  <span className="text-sm font-medium block">{item.label}</span>
                  <span className="text-xs text-muted-foreground">{item.detail}</span>
                </span>
              </span>
              {onNavigateTab && <ExternalLink className="h-4 w-4 text-muted-foreground" />}
            </button>
          ))}
        </CardContent>
      </Card>
    </div>
  )
}
